import React, { FC, memo, useContext } from "react";
import { useTranslation } from "react-i18next";

import { UserContext } from "@/components/User";
import { fetchJobRequestApplications, IPageableJobApplication } from "@/data/job-applications";
import { IJobRequest } from "@/data/job-requests";
import { Flex, Text } from "@/eworkitComponents";
import { IFetcherRenderProps, useFetcher } from "@/hooks/http";
import { RouteButton } from "@/infrastructure/Route";

import { PageHeader } from "../../../components/Page";
import { router } from "../../../router";
import { JobRequestApplicationRecord } from "./JobRequestApplicationRecord";

interface ListProps extends Props, IFetcherRenderProps<IPageableJobApplication> {}

export const MAX_ASIDE_APPLICATIONS = 4;

const JobRequestApplicationListComponent: FC<ListProps> = memo(({ payload }) => {
  const { t } = useTranslation("session");
  const baseClass = "job-request-application-list";

  if (!payload) {
    return null;
  }

  if (payload.content && !payload.content.length) {
    return null;
  }

  return (
    <div className={baseClass}>
      <PageHeader text={`${t("JOB_REQUESTS.DETAIL.ASIDE_HEADER")} (${payload.totalElements})`} />
      <Flex direction="column" innerMargin="md">
        {payload.content.slice(0, MAX_ASIDE_APPLICATIONS).map(record => (
          <JobRequestApplicationRecord key={`job-request-application-${record.id}`} record={record} />
        ))}
        {payload.totalElements > MAX_ASIDE_APPLICATIONS && (
          <Flex justify="center" align="center">
            <RouteButton path={router.get("job-applications").path} variant="primary">
              <Text uppercase weight="bold">
                {t("JOB_APPLICATIONS.LIST.SHOW_MORE_BUTTON")}
              </Text>
            </RouteButton>
          </Flex>
        )}
      </Flex>
    </div>
  );
});

interface Props {
  jobRequest: IJobRequest;
}

export const JobRequestApplications: FC<Props> = memo(({ jobRequest }) => {
  const { contextCompany } = useContext(UserContext);

  const fetcherState = useFetcher(
    fetchJobRequestApplications,
    { jobRequestId: jobRequest.id, companyId: contextCompany && contextCompany.id },
    [contextCompany, jobRequest],
  );

  return <JobRequestApplicationListComponent {...fetcherState} jobRequest={jobRequest} />;
});
